import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { CatalogStereo, ProductView } from '@lemoncloud/codes-goods-api';
import { useStereoIcon } from '@web/app/shared/hooks/useStereoIcon';
import { Workflow } from 'lucide-react';

import { Badge } from '@codes/ui-kit';

import { ProductCardActionMenu } from './ProductCardActionMenu';
import { ProductCardDeploymentStatus } from './ProductCardDeploymentStatus';
import { ProductCardStackDetails } from './ProductCardStackDetails';
import { ProductCardStatus } from './ProductCardStatus';

const getStereoTypeName = (stereo?: CatalogStereo): string => {
    switch (stereo) {
        case 'back':
            return 'Backend API';
        case 'cdk':
            return 'Infrastructure';
        case 'front':
            return 'Frontend';
        case undefined:
        case null:
            return 'Service';
        default:
            return 'Service';
    }
};

interface ProductCardProps {
    product: ProductView;
    isDeploying?: boolean;
    onProductClick?: () => void;
    onSettingsClick?: () => void;
    onEventLogsClick?: () => void;
    onRedeploy?: () => void;
    onDelete?: () => void;
    showChildrenButton?: boolean;
    onViewChildrenClick?: () => void;
    isChild?: boolean;
}

export const ProductCard = ({
    product,
    isDeploying,
    onProductClick,
    onSettingsClick,
    onEventLogsClick,
    onRedeploy,
    onDelete,
    showChildrenButton,
    onViewChildrenClick,
    isChild = false,
}: ProductCardProps) => {
    const { i18n } = useTranslation();
    const getStereoIcon = useStereoIcon();
    const isEnglish = i18n.language === 'en';

    const catalog = product.catalog$;
    const iconImage = catalog?.thumbs?.[0] || getStereoIcon(catalog?.stereo ?? '') || '/assets/icons/empty.svg';

    return (
        <Card className="relative flex flex-col bg-card rounded-xl border-border p-5">
            {/* Header with Status and Action Menu */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <ProductCardStatus status={product.status} progress={product.progress$} state={product.state} />
                    <Badge variant="service" className="bg-box text-sub-title">
                        {getStereoTypeName(product.catalog$?.stereo)}
                    </Badge>
                </div>
                <div className="flex items-center gap-2">
                    {showChildrenButton && !isChild && (
                        <Button
                            variant="ghost"
                            className="p-[2px] w-8 h-8"
                            onClick={e => {
                                e.stopPropagation();
                                onViewChildrenClick?.();
                            }}
                            title="View Child Deployments"
                        >
                            <Workflow size={15} />
                        </Button>
                    )}
                    <ProductCardActionMenu
                        product={product}
                        isDeploying={isDeploying}
                        onSelectResourceDetail={onSettingsClick}
                        onSelectEventLogs={onEventLogsClick}
                        onSelectDeployment={onRedeploy}
                        onDelete={onDelete}
                    />
                </div>
            </div>

            {/* Service Icon and Name */}
            <div className="flex items-center gap-4 mb-4">
                <div className="w-11 h-11 shrink-0 rounded-xl shadow-custom overflow-hidden flex items-center justify-center p-1">
                    <Image src={iconImage} darkSrc={iconImage} alt={product.catalog$?.stereo} />
                </div>
                <div className="flex-1">
                    <div className="text-point text-sm font-medium capitalize">
                        {(product.config$?.stage ?? '').toUpperCase()}
                    </div>
                    <div
                        className={`text-base font-medium ${!isChild ? 'cursor-pointer hover:underline' : ''}`}
                        onClick={
                            !isChild
                                ? e => {
                                      e.stopPropagation();
                                      onProductClick?.();
                                  }
                                : undefined
                        }
                    >
                        {product.name}
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="text-text font-medium text-sm line-clamp-2 mb-4">
                {isEnglish ? product.catalog$?.summaryEn : product.catalog$?.summary}
            </div>

            {/* Deployment Status */}
            <ProductCardDeploymentStatus
                progress={product.progress$}
                status={product.status}
                isDeploying={isDeploying}
                onRedeploy={onRedeploy}
            />

            {/* Service Details - Only show for parent products */}
            {!isChild && (
                <>
                    <div className="my-3 bg-border w-full h-px" />

                    <div className="font-medium text-sm space-y-2 mb-4">
                        {[
                            { label: 'Owner', value: product.owner$?.name || '-', truncate: true },
                            { label: 'Runtime', value: product.config$?.runtime || '-' },
                            {
                                label: 'Region',
                                value: product.region$?.code || product.region$?.name || '-',
                                truncate: true,
                            },
                            { label: 'Type', value: getStereoTypeName(product.catalog$?.stereo), truncate: true },
                        ].map(({ label, value, truncate }) => (
                            <div key={label} className="flex items-center justify-between">
                                <span className="w-20 text-sub">{label}</span>
                                <span className={`text-text ${truncate ? 'truncate text-right' : ''}`}>{value}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Stack Details */}
            <ProductCardStackDetails
                product={product}
                isDeploying={isDeploying || product.status === 'busy' || product.status === 'idle'}
                isChild={isChild}
            />
        </Card>
    );
};
